import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveIgniteProfile } from "./ignite-profile";

/**
 * IGNITE QUIZ -- Control variant of the live Mars Men Heyflow quiz at
 * https://mengotomars.com/pages/quiz-v6 (flow id 5U9hkq9MFvvXQA8VV654).
 *
 * 10 question screens + 4 educational interstitials = 14 screens total.
 * Linear branching for v1; the original Heyflow flow has small conditional
 * jumps that the growth team can layer in later via the `branching` function.
 *
 * Note: this quiz does not screen for GLP-1 use, so the `glp1-user` persona is
 * not reachable from the classifier. Adding a GLP-1 screener is on the polish
 * backlog.
 */

const QUESTION_ORDER = [
  "age",
  "concern",
  "stress-symptoms",
  "energy-3pm",
  "when-started",
  "results-want",
  "info-right-place",
  "body-composition",
  "sleep-hours",
  "info-analyzing",
  "family-history",
  "processed-foods",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "concern",
    type: "single",
    prompt: "What’s your biggest concern about your masculine health right now?",
    options: [
      { value: "energy", label: "Energy levels", emoji: "⚡" },
      { value: "muscle", label: "Muscle mass and strength", emoji: "💪" },
      { value: "sex-drive", label: "Sex drive and performance", emoji: "🔥" },
      { value: "focus", label: "Mental focus and clarity", emoji: "🧠" },
      { value: "weight-loss", label: "Weight loss", emoji: "⚖️" },
    ],
  },
  {
    id: "stress-symptoms",
    type: "single",
    prompt:
      "Do you have stress symptoms during the day? Like feeling tired, cranky, foggy, or moody.",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "How’s your energy at 3pm compared to when you wake up?",
    options: [
      { value: "drained", label: "Completely drained -- need caffeine or a nap", emoji: "🪫" },
      { value: "afternoon-slump", label: "Noticeably lower -- afternoon slump hits hard", emoji: "😴" },
      { value: "slightly-tired", label: "Slightly tired -- still functional but slower", emoji: "😐" },
      { value: "same-energy", label: "Same energy -- no afternoon crash", emoji: "⚡" },
    ],
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you start noticing changes to your energy and drive?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "results-want",
    type: "single",
    prompt: "What results are you expecting from increased testosterone?",
    options: [
      { value: "energy", label: "Higher energy that lasts all day", emoji: "⚡" },
      { value: "muscle", label: "Increased muscle mass and strength", emoji: "💪" },
      { value: "sex-drive", label: "Better sex drive and performance", emoji: "🔥" },
      { value: "focus", label: "Improved mental clarity and focus", emoji: "🧠" },
      { value: "all-of-above", label: "All of the above", emoji: "✨" },
    ],
  },
  {
    id: "info-right-place",
    type: "info",
    heading: "You are in the right place.",
    body: [
      "Over 429,576 men have reclaimed their T, drive, and physique thanks to our new scientific, natural T-revival system.",
    ],
    image: {
      src: "/static/images/before-after.gif",
      alt: "Mars Men customers — before and after",
    },
  },
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your current body composition?",
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Soft with some muscle" },
      { value: "overweight", label: "Overweight / out of shape" },
    ],
  },
  {
    id: "sleep-hours",
    type: "single",
    prompt: "How many hours of quality sleep do you get per night?",
    options: [
      { value: "lt-5", label: "Less than 5 hours" },
      { value: "5-6", label: "5–6 hours" },
      { value: "7-8", label: "7–8 hours" },
      { value: "gt-8", label: "More than 8 hours" },
    ],
  },
  {
    id: "info-analyzing",
    type: "info",
    heading: "Analyzing your health profile…",
    body: [
      "Based on your responses, we’re building a comprehensive assessment of your hormonal health and identifying the key factors affecting your energy and vitality.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
  {
    id: "family-history",
    type: "single",
    prompt:
      "Do you have a family history of diabetes, heart disease, or metabolic issues?",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "not-sure", label: "Not sure" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "These conditions share similar root causes with low testosterone — insulin resistance, inflammation, and metabolic dysfunction. Family patterns help us identify your risk factors.",
    },
  },
  {
    id: "processed-foods",
    type: "single",
    prompt: "How often do you eat processed foods?",
    options: [
      { value: "daily", label: "Daily -- most meals include processed foods" },
      { value: "several-week", label: "Several times a week" },
      { value: "occasionally", label: "Occasionally -- mostly whole foods" },
      { value: "rarely", label: "Rarely -- I avoid processed foods" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Processed foods contain chemicals and additives that disrupt hormone production. Ultra-processed foods are linked to lower testosterone levels in multiple studies.",
    },
  },
];

export const igniteQuiz = defineQuiz({
  id: "ignite",
  slug: "ignite",
  experiment: {
    intelligemsTestId: "c262aae5-442d-4314-8d98-f92c17fe2c70",
    name: "MM-0064 Shoppable Quiz",
  },
  meta: {
    title: "What’s Really Happening With Your Testosterone",
    introHeadline: "Find Out What’s Really Happening With Your Testosterone",
    introBody:
      "This 2-minute assessment analyzes your symptoms, lifestyle, and risk factor in order to find a solution.",
    testimonial: {
      quote:
        "Took maybe a minute. Let’s just say me and my wife are VERY happy I took this quiz.",
      author: "ADAM, Verified Customer",
      variantTag: "control",
    },
  },
  questions,
  capture: {
    fields: ["email"],
    required: ["email"],
    headline: "See your personalized result.",
    subheadline:
      "We’ll send your protocol summary to your inbox so you can act on it when you’re ready.",
    cta: "Show my result",
    disclaimer: "We hate junk mail too. Unsubscribe in one click.",
  },

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const concern = answers["concern"];
    const energy3pm = answers["energy-3pm"];
    const whenStarted = answers["when-started"];
    const bodyComp = answers["body-composition"];
    const sleep = answers["sleep-hours"];
    const family = answers["family-history"];
    const age = answers["age"];

    // Performance-focused: lean / muscular body, sleeps well, primary concern is muscle.
    if (
      bodyComp === "lean-muscular" &&
      (sleep === "7-8" || sleep === "gt-8") &&
      (concern === "muscle" || concern === "focus")
    ) {
      return "performance";
    }

    // TRT-alternative: long-running issues + completely drained + family risk markers.
    if (
      whenStarted === "over-year" &&
      energy3pm === "drained" &&
      (family === "yes" || age === "50-plus")
    ) {
      return "trt-alternative";
    }

    // Skeptical buyer: not sure when it started or no clear concerns surfacing.
    if (whenStarted === "not-sure" || concern === "weight-loss") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with body softening, sleep issues, or stress symptoms.
    if (
      (age === "50-plus" || age === "40-49") &&
      (bodyComp === "soft-some-muscle" || bodyComp === "overweight" || sleep === "lt-5")
    ) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveIgniteProfile,
});
